import { Component, EventEmitter, Input, OnInit, Output, ViewChild, TemplateRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TokenService } from 'services/token/token.service';
import { MixpanelService } from 'services/mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from 'constants/mixpanel-events.constants';
import { PasswordValidators } from 'validators/password-validators';
import { HttpError } from 'services/http/http-error.interface';
import { email, equalTo, reduceTo, required, textRangeLength, withMessage } from '@certemy/common';
import { getErrorMessage, getMergedFormErrors } from 'utils/helpers';
import {
  getSelfEnrollmentLink,
  LOGIN_ERRORS,
  RESPONSE_ERRORS,
  SIGN_IN_TYPES,
  SIGN_UP_TYPES,
  VerifyTokenData,
} from 'pages/entry/entry.helpers';
import _get from 'lodash-es/get';
import { REGISTER_AS_ERRORS, VALIDATION_ERRORS, VALIDATION_MESSAGES, RegisterData } from './register-as.constants';
import { Observable, switchMap, filter, NEVER, defer, of } from 'rxjs';
import { ExternalLoginSettings } from 'interfaces/external-login.interface';
import { Selectors as LoginProviderSelectors } from 'services/login-provider/+state/login-provider.state';
import { map, take, tap, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginProviderRedirectResponse } from 'services/login-provider/login-provider.interface';
import { SmartForm, SmartModalService } from '@certemy/ui-components';
import _size from 'lodash-es/size';
import _sortBy from 'lodash-es/sortBy';

const requiredWithMessage = withMessage('Please fill out all fields', required);

@Component({
  selector: 'cm-register-as',
  templateUrl: './register-as.component.html',
  styleUrls: ['./register-as.component.scss'],
})
export class RegisterAsComponent implements OnInit {
  @Input() roleFromInput;
  @Input() token;
  @Input() title;
  @Input() isSelfEnrollment: boolean = false;
  @Input() hideTitle: boolean = false;
  @Input() organizationRoles: SmartForm.FieldOption[];

  settings$: Observable<ExternalLoginSettings> = this.store
    .select(LoginProviderSelectors.loginProviderMappedState)
    .pipe(
      map(data => ({
        ...data,
        url: window.location.href,
      })),
    );

  fieldValues: any[] = [];
  submitted = false;
  form: UntypedFormGroup;
  role: string = null;
  errorMessages: string[] = [];
  warningMessage = '';
  @Output() callback = new EventEmitter();
  loginType: SIGN_IN_TYPES = SIGN_IN_TYPES.DEFAULT;
  private showPasswords = true;
  showExternalLogin = true;
  readonly SIGN_IN_TYPES = SIGN_IN_TYPES;

  @ViewChild('warningTemplate') warningTemplate: TemplateRef<any>;

  constructor(
    private tokenService: TokenService,
    private mixpanel: MixpanelService,
    private store: Store<any>,
    private route: ActivatedRoute,
    private smartModalService: SmartModalService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.role = this.roleFromInput.toLowerCase();
    this.mixpanel.track(MIXPANEL_EVENTS.CREATE_ACCOUNT_FORM_VIEW, { Role: this.role });
    const passwordValidator = reduceTo('invalidPassword', [
      Validators.minLength(8),
      PasswordValidators.oneDigitRule(),
      PasswordValidators.lowercaseCharacterRule(),
      PasswordValidators.uppercaseCharacterRule(),
    ]);
    const password: UntypedFormControl = new UntypedFormControl('', [
      requiredWithMessage,
      withMessage(
        `Password should contain at least 8 characters and consist of
       a combination of uppercase letters, lowercase letters, numbers and special characters.`,
        passwordValidator,
      ),
    ]);
    const confirm_password: UntypedFormControl = new UntypedFormControl('', [
      requiredWithMessage,
      withMessage('Password does not match the confirm password', equalTo(password)),
    ]);

    const generalFieldValues = [
      {
        label: 'First Name:',
        type: 'text',
        value: 'first_name',
        control: new UntypedFormControl('', [requiredWithMessage, textRangeLength('First Name')]),
      },
      {
        label: 'Last Name:',
        type: 'text',
        value: 'last_name',
        control: new UntypedFormControl('', [requiredWithMessage, textRangeLength('Last Name')]),
      },
      {
        label: 'Email:',
        type: 'email',
        value: 'email',
        control: new UntypedFormControl('', [
          requiredWithMessage,
          withMessage('Please enter the valid email', email),
          textRangeLength('Email'),
        ]),
      },
      { label: 'Password:', type: 'password', value: 'password', control: password },
      { label: 'Confirm Password:', type: 'password', value: 'confirm_password', control: confirm_password },
    ];

    switch (this.roleFromInput) {
      case SIGN_UP_TYPES.ORGANIZATION:
        this.fieldValues = [
          {
            label: 'Organization Name:',
            type: 'text',
            value: 'organization_name',
            control: new UntypedFormControl('', [requiredWithMessage, textRangeLength('Organization Name')]),
          },
          ...generalFieldValues,
        ];
        this.form = this.buildForm(this.fieldValues);
        break;
      case SIGN_UP_TYPES.PROFESSIONAL:
        if (_size(this.organizationRoles) > 1) {
          const organizationRoleControl = {
            label: 'Role:',
            type: 'select',
            value: 'organizationRoleId',
            options: _sortBy(this.organizationRoles, ['name']),
            control: new UntypedFormControl(null, requiredWithMessage),
          };
          generalFieldValues.splice(2, 0, organizationRoleControl);
        }
        this.fieldValues = generalFieldValues;
        this.form = this.buildForm(this.fieldValues);
        break;
      default:
        this.errorMessages = ['Unknown role, please try again.'];
        break;
    }
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams) {
      const { accessToken, type, organizationId, error } = queryParams as LoginProviderRedirectResponse;
      if ([SIGN_IN_TYPES.ONE_LOGIN, SIGN_IN_TYPES.OKTA, SIGN_IN_TYPES.AZURE, SIGN_IN_TYPES.ENTRA, SIGN_IN_TYPES.PING].includes(type)) {
        if (error === LOGIN_ERRORS.ACCESS_DENIED) {
          this.errorMessages = [LOGIN_ERRORS.INCORRECT_ACCOUNT];
        } else if (accessToken) {
          this.verifyToken(accessToken, type, Number(organizationId));
        }
      }
    }
  }

  isProfessionalRole() {
    return this.roleFromInput === SIGN_UP_TYPES.PROFESSIONAL;
  }

  isOrganizationRole() {
    return this.roleFromInput === SIGN_UP_TYPES.ORGANIZATION;
  }

  async handleCredentialResponse(response) {
    const { credential, type } = response;
    await this.verifyToken(credential, type);
  }

  private verifyToken(token: string, type: SIGN_IN_TYPES, organizationId?: number) {
    this.tokenService.verifyUserByToken(token, type, organizationId).subscribe((result: VerifyTokenData) => {
      this.form.patchValue({
        first_name: result.firstName,
        last_name: result.lastName,
        email: result.email,
        password: 'nullN_11',
        confirm_password: 'nullN_11',
      });
      this.form.get('email').disable();
      this.form.get('password').disable();
      this.form.get('confirm_password').disable();
      this.loginType = type;
      this.showPasswords = false;
      this.showExternalLogin = false;
    });
  }

  private _isFormValid(form: UntypedFormGroup): boolean {
    if (form.valid) {
      return true;
    } else {
      const validationMessage =
        getMergedFormErrors(this.form)
          .map(errors => Object.values(errors).find(el => typeof el === 'string'))
          .filter(hasValidationMessage => !!hasValidationMessage)[0] || 'Please fill out all fields';
      this.updateErrors(validationMessage);
    }
  }

  private buildForm(fieldValues: { value: string; control: UntypedFormControl }[]): UntypedFormGroup {
    return new UntypedFormGroup(
      fieldValues.reduce(
        (form, field) => ({
          ...form,
          [field.value]: field.control,
        }),
        {},
      ),
    );
  }

  showField(fieldValue: any) {
    switch (fieldValue.value) {
      case 'password':
      case 'confirm_password':
        return this.showPasswords;

      default:
        return true;
    }
  }

  onSubmit(value: any) {
    this.clearErrors();
    this.submitted = true;
    let registrationUrl = '';

    value.signin_type = this.loginType;
    value.email = this.form.get('email').value;
    value.password = this.form.get('password').value;
    value.confirm_password = this.form.get('confirm_password').value;

    if (this.token) {
      value.link = getSelfEnrollmentLink();
      registrationUrl = '/self_enrollment';
    } else {
      registrationUrl = `/auth`;
      value.signUpType = this.isProfessionalRole() ? SIGN_UP_TYPES.PROFESSIONAL : SIGN_UP_TYPES.ORGANIZATION;
    }
    if (this._isFormValid(this.form)) {
      const message: any = {
        msg: `${this.role}`,
        email: value.email,
        payload: value,
      };
      defer(() => this.token ? this.validateUserSelfEnrollmentLogin(value) : of(null))
        .pipe(
          switchMap(() =>
            this.tokenService.signUpUser(`${registrationUrl}/register`, value))).subscribe(
        data => {
          this.callback.emit({ success: true, message, error: null });
        },
        (error: HttpError) => {
          const errMsg = error.body;
          if (errMsg && errMsg.code_name === RESPONSE_ERRORS.SIGN_UP_ERROR) {
            if (errMsg.is_user_deleted) {
              this.callback.emit({
                success: false,
                message: { msg: 'ACCOUNT_DELETED', email: 'john@doe.com' },
                error: REGISTER_AS_ERRORS.USER_DELETED,
              });
            } else {
              message.isSelfEnrollment = errMsg.is_self_enrollment;
              message.newProfile = errMsg.is_new_profile_should_be_created;
              message.organization_sign_up_data_id = errMsg.organization_sign_up_data_id;
              message.payload = {
                ...message.payload,
                ...(this.isSelfEnrollment ? { link: getSelfEnrollmentLink() } : {}),
              };
              message.uuid = this.token;
              message.title = this.title;
              this.callback.emit({ success: false, message, error: REGISTER_AS_ERRORS.EXISTING_ACCOUNT });
            }
          } else if (errMsg && errMsg.code_name === RESPONSE_ERRORS.VALIDATION_ERROR) {
            const invalidLinkError = !!_get(errMsg, 'error_details.link.length', 0);

            if (typeof errMsg.error_details === 'string') {
              this.updateErrors(getErrorMessage(error, 'Validation error'));
              return;
            }

            if (invalidLinkError) {
              this.callback.emit({ success: false, message: {}, error: REGISTER_AS_ERRORS.LINK_NOT_FOUND });
            }

            this.updateErrors(getErrorMessage(error, 'Validation error'));
          } else {
            this.errorMessages = ['Unknown error'];
          }
        },
      );
    }
  }

  validateUserSelfEnrollmentLogin(value: RegisterData): Observable<boolean> {
    return this.tokenService.validateUserSelfEnrollmentLogin({
      firstName: value.first_name,
      lastName: value.last_name,
      email: value.email,
      link: value.link,
    }).pipe(
      catchError((error: HttpError) => {
        const errBody = error.body;
        if (errBody?.error_details?.[VALIDATION_ERRORS.DUPLICATE_ACCOUNT]) {
          const [{ organizationName = 'Certemy' }, ...rest] =
            errBody?.error_details[VALIDATION_ERRORS.DUPLICATE_ACCOUNT];
          this.warningMessage = this.getWarningMessage(VALIDATION_ERRORS.DUPLICATE_ACCOUNT, organizationName);
          return this.smartModalService.openConfirm({
            title: 'WARNING',
            body: this.warningTemplate,
            rejectButtonText: 'Go To Login',
            successButtonText: 'Continue – Create Account',
          }, { size: 'lg' })
            .pipe(
              take(1),
              tap((result: boolean) => {
                if (!result) {
                  this.router.navigateByUrl(`/entry/self-enrollments/${this.title}/${this.token}/login`);
                }
              }),
              filter(result => result),
            );
        }

        this.errorMessages = ['Unknown error'];
        return NEVER;
      }),
    );
  }

  updateErrors(error: string): boolean {
    if (this.errorMessages.indexOf(error) === -1) {
      this.errorMessages.push(error);
      return true;
    }
    return false;
  }

  clearErrors(): boolean {
    this.errorMessages = [];
    return true;
  }

  getWarningMessage(errorType: string, text?: string) {
    if (errorType === VALIDATION_ERRORS.DUPLICATE_ACCOUNT) {
      return VALIDATION_MESSAGES.DUPLICATE_ACCOUNT.replace(/{organizationName}/g, text);
    }
  }

  redirectToLogin() {
    this.router.navigateByUrl(`/entry/self-enrollments/${this.title}/${this.token}/login`);
  }
}
